/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-summary.helper.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import i18n from 'i18next';
import moment from 'moment';
import { STUDY_SUMMARY_GRIDS } from '@components/central-monitoring/study-summary/study-summary.data';
import { IStatisticsData } from '@components/core/statistics-bar/statistics-bar.interface';
import Constants from '@utils/constants';
import { formatDateUTC_DEPRECATED } from '@utils/convertor';
import { isSplittedValue } from '@utils/validation';
// eslint-disable-next-line max-len
import { centralMonitoringReviewData_OpraQueries_centralMonitoringReviewData_CentralMonitoringReviewData_studyReview_StudyReviewData } from '@queries/generated/central-monitoring.generated';
import { StudyRestrictedFields } from '@queries/generated/fragments.generated';

export const formSummaryData = (studyConfig: any, studySummary: any, datasetDate: any) => {
    const data: IStatisticsData[] = [];

    if (studyConfig?.numEnrolled) {
        data.push({ name: i18n.t('cm:enrolledSubjects'), value: studySummary.enrolledSubjects });
    }
    if (studyConfig?.numScreened) {
        data.push({ name: i18n.t('cm:screenedSubjects'), value: studySummary.screenedSubjects });
    }
    if (studyConfig?.numScreenFailed) {
        data.push({ name: i18n.t('cm:screenedFailedSubjects'), value: studySummary.screenedFailedSubjects });
    }
    if (studyConfig?.numRandomised) {
        data.push({ name: i18n.t('cm:#randomised'), value: studySummary.randomisedSubjects });
    }
    if (studyConfig?.numTreatmentDiscontinued) {
        data.push({
            name: i18n.t('cm:treatmentDiscontinuedSubjects'),
            value: studySummary.treatmentDiscontinuedSubjects,
        });
    }
    if (studyConfig?.numStudyDiscontinued) {
        data.push({ name: i18n.t('cm:discontinuedSubjects'), value: studySummary.discontinuedSubjects });
    }
    if (studyConfig?.numCompleted) {
        data.push({ name: i18n.t('cm:numCompleted'), value: studySummary.numCompleted });
    }
    if (datasetDate?.isCMR) {
        data.push({
            name: i18n.t('admin:reportGeneratedDate'),
            value: String(formatDateUTC_DEPRECATED(datasetDate?.created, true, 'DD-MMM-YYYY HH:mm')),
        });
    }

    return data;
};

export const getStudySummaryInitialGrid = (study: StudyRestrictedFields) => {
    switch (true) {
        case study?.displayRiskOverviewOnStudySummary:
            return STUDY_SUMMARY_GRIDS.risk;
        case study?.displayStudyOverviewOnStudySummary:
            return STUDY_SUMMARY_GRIDS.study;
        default:
            return STUDY_SUMMARY_GRIDS.site;
    }
};

export const formReviewData = (
    reviewData: centralMonitoringReviewData_OpraQueries_centralMonitoringReviewData_CentralMonitoringReviewData_studyReview_StudyReviewData,
) => [
    {
        label: i18n.t('cm:reviewed'),
        text: reviewData?.reviewed ? i18n.t('common:yes') : i18n.t('common:no'),
    },
    {
        label: i18n.t('reports:reviewer'),
        text: reviewData?.reviewer || i18n.t('ra:n/a'),
    },
    {
        label: i18n.t('cm:reviewStartDate'),
        text: reviewData?.reviewStartDate || i18n.t('ra:n/a'),
    },
    {
        label: i18n.t('cm:reviewCompleteDate'),
        text: reviewData?.reviewCompleteDate || i18n.t('ra:n/a'),
    },
    {
        label: i18n.t('cm:summary'),
        text: reviewData?.reviewSummary || i18n.t('ra:n/a'),
        scrollable: true,
    },
];

export const getFilterType = (type: any) => {
    switch (type) {
        case Constants.FieldDefinitionFieldTextTypes.date:
        case Constants.FieldDefinitionFieldTextTypes.partialDate:
            return 'date';
        case Constants.FieldDefinitionFieldTextTypes.int:
        case Constants.FieldDefinitionFieldTextTypes.float:
            return 'numeric';
        case Constants.FieldDefinitionFieldTextTypes.bool:
        case Constants.FieldDefinitionFieldTextTypes.text:
        case '':
        case undefined:
        default:
            return 'text';
    }
};

export const createDatePipe = (value: string) => {
    const currentDate = moment.utc(value).toISOString();
    const dateFormat = moment(value, 'DD-MMM-YYYY', true)?.isValid()
        ? 'DD-MMM-YYYY'
        : moment(value, 'MMM-YYYY', true)?.isValid()
          ? 'MMM-YYYY'
          : null;

    return `${currentDate}|${dateFormat}`;
};

export const getReportDateFormat = (dateString: string) => {
    const dateResult = isSplittedValue(dateString) ? dateString.split('|')[0] : dateString;
    const dateFormatResult = isSplittedValue(dateString) ? dateString.split('|')[1] : '';

    return dateResult ? moment(dateResult).format(dateFormatResult) : '';
};
